import * as React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { Provider } from 'react-redux';
import { SnackbarProvider } from 'notistack';
import '@patternfly/react-core/dist/styles/base.css';
import Login from '@app/pages/auth/Login';
import ForgotPassword from '@app/pages/auth/ForgotPassword';
import NewPasswordChallenge from '@app/pages/auth/NewPasswordChallenge';
import { AppLayout } from '@app/components/layout/layout.component';
import { AppRoutes } from '@app/routes';
import CssBaseline from '@mui/material/CssBaseline';

import { ConnectedRouter } from 'connected-react-router';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryParamProvider } from 'use-query-params';

import { Html } from './components/layout/html.component';
import { SearchProvider } from './contexts/search.context';
import CombinedProvider from './providers/CombinedProvider';
import { CartProvider } from './contexts/cart.context';

import { history, persistor, store } from './store';
import UserSession from './common/user-session';
import { actionCreators } from './common/auth/auth.reducer';

import '@app/app.css';
import './styles/reduction.scss';
// Import CSS to hide metrics temporarily
import './styles/metrics-hide.css';

// Inject Maps script early to prevent API calls on refresh
const injectGoogleMapsScript = () => {
  // Check if script already exists
  const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
  if (existingScript) {
    return;
  }

  const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
  if (apiKey) {
    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&region=CA`;
    script.onload = () => {

    };
    script.onerror = () => {
      console.error('Maps script failed to load');
    };
    document.head.appendChild(script);
  }
};

// Inject Google Maps immediately when app loads
injectGoogleMapsScript();

const basePath = process.env.REACT_APP_BASE_PATH || '';

// Check and refresh token on app load if needed - but only if not on auth pages
const checkAndRefreshToken = () => {
  // Don't refresh tokens if user is on authentication pages
  const currentPath = window.location.pathname;
  const basePath = process.env.REACT_APP_BASE_PATH || '';
  const authPaths = [
    `${basePath}/auth/login`,
    `${basePath}/login`,
    `${basePath}/auth/forgot-password`,
    `${basePath}/forgot-password`,
    `${basePath}/auth/new-password`,
    `${basePath}/new-password`
  ];
  
  if (authPaths.some(path => currentPath === path || currentPath.endsWith(path))) {
    console.log('Skipping token refresh - user is on authentication page');
    return;
  }
  
  const token = UserSession.getParam('refreshToken');
  const expiration = UserSession.getParam('expiration');
  
  if (token && expiration) {
    const currentTime = new Date();
    const expireTime = new Date(expiration);
    
    // If token exists but is expired or about to expire (within 5 minutes), try refreshing
    if (currentTime > expireTime || 
        (expireTime.getTime() - currentTime.getTime()) < 5 * 60 * 1000) {
      console.log('Token refresh needed on app load');
      store.dispatch(actionCreators.tokenRefreshRequest());
    }
  }
};

// Execute token check
checkAndRefreshToken();

const App: React.FunctionComponent = () => (
  <React.Fragment>
    <Provider store={store}>
        <PersistGate persistor={persistor} loading={null}>
          <ConnectedRouter history={history}>
            <QueryParamProvider>
              <SnackbarProvider dense anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Html>
                  <Switch>
                    {/* Auth routes - completely isolated from main app providers */}
                    <Route exact path={`${basePath}/forgot-password`}>
                      <Redirect to={`${basePath}/auth/forgot-password`} />
                    </Route>
                    <Route exact path={`${basePath}/auth/forgot-password`}>
                      <ForgotPassword />
                    </Route>
                    <Route exact path={`${basePath}/new-password`}>
                      <Redirect to={`${basePath}/auth/new-password`} />
                    </Route>
                    <Route exact path={`${basePath}/auth/new-password`}>
                      <NewPasswordChallenge />
                    </Route>
                    <Route exact path={`${basePath}/login`}>
                      <Redirect to={`${basePath}/auth/login`} />
                    </Route>
                    <Route exact path={`${basePath}/auth/login`}>
                      <Login />
                    </Route>
                    <Route exact path={`${basePath}/`}>
                      <Redirect to={`${basePath}/dashboard`} />
                    </Route>
                    {/* Main app routes - with authentication providers */}
                    <Route>
                      <CombinedProvider>
                        <CartProvider>
                          <SearchProvider>
                            <AppLayout>
                              <AppRoutes />
                            </AppLayout>
                          </SearchProvider>
                        </CartProvider>
                      </CombinedProvider>
                    </Route>
                  </Switch>
                </Html>
              </SnackbarProvider>
            </QueryParamProvider>
          </ConnectedRouter>
        </PersistGate>
      </Provider>
  </React.Fragment>
);

export default App;
